import { QueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { actAsCacheKey } from "../../contexts/actAsStore";
import { enviroment } from "../../enviroment";

// Kept out of LogoRepository on purpose: that module imports types from
// useLogoActionsHandler, and the actions handler needs these helpers - a
// separate leaf module avoids an import cycle.

// Detail cache key under the CURRENT act-as scope - the exact shape
// LogoRepository.useLogo reads (["logos", actAs, id]).
export function logoDetailKey(logoId: BaseLogo["id"]): (string | number)[] {
  return ["logos", actAsCacheKey(), logoId];
}

// Explicit "no genuine high-res yet" mark on a cached detail entry (flag
// REACT_APP_LOGO_HEAL_PER_MUTATION). The copy response carries the 180px
// processed blob AS highResUrl (a data-URI that differs from lowResUrl), so
// the heal's equality rule never sees a clone as pending; the clone flows
// stamp the seeded entry instead and isHighResPending honours the field.
// No-op when the entry does not exist (never creates a fabricated entry).
export function markDetailHighResPending(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
): void {
  const key = logoDetailKey(logoId);
  const current = queryClient.getQueryData<BaseLogo>(key);
  if (!current || !current.displayImage) return;
  if (current.displayImage.highResPending === true) return;
  queryClient.setQueryData<BaseLogo>(key, {
    ...current,
    displayImage: { ...current.displayImage, highResPending: true },
  });
}

// Flag REACT_APP_LOGO_HEAL_PER_MUTATION: drop a heal poll (GetLogo) that is
// still in flight for this logo right before a mutation response is seeded.
// Without this a poll started BEFORE the mutation can land AFTER its
// response, overwrite the seeded entry with the pre-mutation render and -
// because that render may carry a genuine high-res - stop the heal, so the
// synced canvas would show the old colours/size until F5. react-query
// ignores the result of a cancelled fetch and reverts the entry to its
// pre-fetch state, which the seed then replaces. No-op when nothing is in
// flight; a no-op with the flag off (the canvas is not synced then).
export function cancelInFlightDetailFetch(
  queryClient: QueryClient,
  logoId: BaseLogo["id"],
): void {
  if (!enviroment.logoHealPerMutationEnabled) return;
  void queryClient.cancelQueries({
    queryKey: logoDetailKey(logoId),
    exact: true,
  });
}
